/**
 * Client detail page (the operator's tenant surface).
 *
 * A deep-linkable route (/admin/clients/:clientId) rather than an
 * inline panel, so it survives a direct load and a refresh.
 *
 * Six sections:
 *   1. the editable client fields (shared with the create form)
 *   2. standard learning content provisioning and repair
 *   3. the client's cohorts — the segments its people are grouped into,
 *      and where the start and end dates actually live (a client runs
 *      several engagements, so the window is never on the tenant)
 *   3. current enrollment periods per tier — read-only operational information
 *   4. the roster — reusing the GLOBAL students endpoint filtered to
 *      this client, not a second student list of its own
 *   5. the client's HR users and their provisioning entrypoint
 *
 * plus a soft delete, which the server refuses while students remain.
 */
import { SectionHeader } from '../common/section-header'
import { PageHeader } from '../components/page-header'
import { HighConsequenceActionPanel } from '../common/high-consequence-action-panel'
import { OPERATOR_DETAIL_PAGE, OPERATOR_PAGE_MAIN } from '../common/page-layout'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  buttonVariants,
  FieldHelp,
  Notice,
} from '@speaking-coach/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '../api/query-keys'
import { PREVIEW_CELL, PreviewNameLink, PreviewTable } from '../common/preview-table'
import { TABLE_ROW_NESTED } from '../common/table'
import { Link, useParams } from 'react-router-dom'
import { adminCohortClient, messageForCohortError } from '../cohorts/api'
import { InternalBadge } from '../cohorts/cohort-form-fields'
import { adminStudentClient } from '../students/api'
import { InviteStateCell } from '../students/invite-state-cell'
import { HistoryLink } from '../activity/history-link'
import { adminClientsApi, messageForClientError } from './api'
import {
  InternalField,
  CompanyInformationSetting,
  NameField,
  SlugField,
  TrophyTargetField,
} from './client-form-fields'
import { ENROLLMENT_TIERS } from './types'
import { SaveButton } from '../common/save-button'
import { UnsavedChangesGuard } from '../common/unsaved-changes'
import { updateClientSchema, type UpdateClientForm } from './client-schema'
import type { ClientDetail } from './types'

type LoadStatus = 'loading' | 'loaded' | 'error'

/** How many roster rows to show inline before pointing at the full list. */
const ROSTER_PREVIEW = 10

/** Same preview cap as the roster and the cohort's own member list. */
const COHORT_PREVIEW = 10

export function ClientDetailPage(): JSX.Element {
  const { clientId = '' } = useParams()
  const clientQuery = useQuery({
    queryKey: queryKeys.client(clientId),
    queryFn: () => adminClientsApi.get(clientId),
  })

  const client = clientQuery.data ?? null
  const status: LoadStatus = clientQuery.isPending
    ? 'loading'
    : clientQuery.isError
      ? 'error'
      : 'loaded'
  const loadError =
    clientQuery.error !== null
      ? messageForClientError(clientQuery.error)
      : null

  return (
    <main className={OPERATOR_PAGE_MAIN}>
      <div className={OPERATOR_DETAIL_PAGE}>
        {status === 'loading' && (
          <p className="text-text-secondary mt-4">Loading client…</p>
        )}
        {status === 'error' && (
          <div
            role="alert"
            className="mt-4 bg-surface-card border border-danger/40 p-4 rounded-token-card"
          >
            <p className="font-medium">Could not load client</p>
            <p className="text-text-secondary text-body mt-1">{loadError}</p>
          </div>
        )}
        {status === 'loaded' && client && (
          <>
            {/* No slug in the header meta: it lives in the identity card
                right below, and the sticky bar is fighting for width
                with the trail and actions. */}
            <PageHeader
              title={client.name}
              meta={
                client.isInternal ? (
                  <span
                    className="font-normal"
                    title="Internal: excluded from customer analytics. Not an access flag."
                  >
                    <InternalBadge />
                  </span>
                ) : undefined
              }
              action={<HistoryLink to={`/admin/clients/${clientId}/history`} />}
            />
            <EditSection client={client} />
            <TranscriptImprovementConsentSection client={client} />
            {/* Cohorts above the roster: an operator creates the cohort,
                then puts people in it. */}
            <CohortsSection clientId={client.clientId} />
            <CurrentEnrollmentTiersSection client={client} />
            <RosterSection client={client} />
            <HrUsersSection client={client} />
            <DangerSection client={client} />
          </>
        )}
      </div>
    </main>
  )
}

// ------------------------------------------------------------- Edit

/** The client row as the edit form holds it. */
function editFormValues(client: ClientDetail): UpdateClientForm {
  return {
    name: client.name,
    slug: client.slug,
    contractTrophyTarget: client.contractTrophyTarget,
    isInternal: client.isInternal,
    clientEmployeeIdEnabled: client.clientEmployeeIdEnabled ?? false,
    clientDepartmentEnabled: client.clientDepartmentEnabled ?? false,
  }
}

function EditSection({ client }: { client: ClientDetail }): JSX.Element {
  const queryClient = useQueryClient()

  // zod + RHF (ADMIN-FORMS), sharing `client-schema.ts` with the create
  // form so the two cannot disagree about what a valid client is.
  const form = useForm<UpdateClientForm>({
    resolver: zodResolver(updateClientSchema),
    mode: 'onChange',
    defaultValues: editFormValues(client),
  })
  const { control, handleSubmit, formState } = form
  const errors = formState.errors

  // A student who can log in locks the slug server-side. `studentCount`
  // is the closest signal this response carries, so the field is
  // presented as locked once anyone is on the roster; the server is
  // still the authority and its 409 surfaces below.
  const slugLocked = client.studentCount > 0

  const save = useMutation({
    mutationFn: (values: UpdateClientForm) =>
      adminClientsApi.update(client.clientId, {
        name: values.name,
        // Omit the slug entirely when it is locked — sending the current
        // value would be accepted, but not sending it is honest about
        // what the form is offering.
        ...(slugLocked ? {} : { slug: values.slug }),
        contractTrophyTarget: values.contractTrophyTarget,
        isInternal: values.isInternal,
        clientEmployeeIdEnabled: values.clientEmployeeIdEnabled,
        clientDepartmentEnabled: values.clientDepartmentEnabled,
      }),
    onSuccess: (updated) => {
      // Seed the detail cache with the response we already hold rather
      // than invalidating and refetching it — the PATCH returns the same
      // shape the GET does. The client LIST still needs invalidating: a
      // renamed client shows there too.
      queryClient.setQueryData(queryKeys.client(client.clientId), updated)
      void queryClient.invalidateQueries({ queryKey: queryKeys.clientLists })
      // The fresh row clears `isDirty`, which re-disables Save and puts
      // "Saved ✓" on the button.
      form.reset(editFormValues(updated))
    },
  })

  const saving = save.isPending
  const saveError =
    save.error !== null ? messageForClientError(save.error) : null

  const onValid = (values: UpdateClientForm): void => {
    // The button is disabled for a clean form too, but Enter in a text
    // field submits past a disabled button.
    if (!formState.isDirty || saving) return
    save.mutate(values)
  }

  return (
    <section
      aria-label={`Edit client ${client.name}`}
      className="bg-surface-card rounded-token-card p-6"
    >
      <UnsavedChangesGuard
        when={formState.isDirty}
        name={`client ${client.name}`}
      />
      <form
        onSubmit={handleSubmit(onValid)}
        aria-label="Client details"
        className="space-y-4"
        noValidate
      >
        {/* `noValidate`: the zod schema is the authority — native constraint
            validation would pre-empt it with the browser's bubble, and in
            jsdom it blocks submission outright so `handleSubmit` never
            runs. */}
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <NameField
              value={field.value}
              onChange={field.onChange}
              error={errors.name?.message ?? null}
            />
          )}
        />
        <Controller
          control={control}
          name="slug"
          render={({ field }) => (
            <SlugField
              value={field.value}
              onChange={field.onChange}
              locked={slugLocked}
              error={errors.slug?.message ?? null}
            />
          )}
        />
        <Controller
          control={control}
          name="contractTrophyTarget"
          render={({ field }) => (
            <TrophyTargetField
              value={field.value}
              onChange={field.onChange}
              error={errors.contractTrophyTarget?.message ?? null}
            />
          )}
        />
        <fieldset className="border-b border-border-default pb-4">
          <legend className="font-medium">Company information</legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            <Controller
              control={control}
              name="clientEmployeeIdEnabled"
              render={({ field }) => (
                <CompanyInformationSetting field="client-employee-enabled" label="Employee ID" value={field.value ?? false} onChange={field.onChange} />
              )}
            />
            <Controller
              control={control}
              name="clientDepartmentEnabled"
              render={({ field }) => (
                <CompanyInformationSetting field="client-department-enabled" label="Department" value={field.value ?? false} onChange={field.onChange} />
              )}
            />
          </div>
        </fieldset>
        <Controller
          control={control}
          name="isInternal"
          render={({ field }) => (
            <InternalField value={field.value} onChange={field.onChange} />
          )}
        />

        {saveError && (
          <Notice variant="danger">
            {saveError}
          </Notice>
        )}

        {/* The confirmation lives ON the button ("Saved ✓") rather than in a
            line that pushes the layout down; a clean form's Save is disabled
            because re-saving an unchanged row is never what an operator
            means. */}
        <SaveButton
          pending={saving}
          dirty={formState.isDirty}
          justSaved={save.isSuccess}
          disabled={!formState.isValid}
        />
      </form>
    </section>
  )
}

function TranscriptImprovementConsentSection({
  client,
}: {
  client: ClientDetail
}): JSX.Element {
  const consented = client.transcriptImprovementConsent
  return (
    <section
      aria-label="Data use"
      className="mt-4 rounded-token-card bg-surface-card p-6"
    >
      <SectionHeader
        title="Data use"
        meta={<Badge
          variant={consented ? 'neutral' : 'muted'}
          className={
            consented
              ? 'shrink-0 border-accent bg-accent-soft text-text-primary'
              : 'shrink-0'
          }
        >
          {consented ? 'Consented' : 'Not consented'}
        </Badge>}
      />
      <p className="mt-2 text-body font-medium">
        Transcript improvement consent
      </p>
      <p className="mt-1 max-w-2xl text-body text-text-secondary">
        Consent permits retention of identity-stripped text from completed
        calls, plus call and lesson metrics, to improve the speaking coach.
        Raw audio is not retained, and this data is not exposed in
        customer-facing surfaces.
      </p>
      <p className="mt-3 text-caption text-text-secondary">
        Read-only. Consent changes are not managed from Admin.
      </p>
    </section>
  )
}

// ---------------------------------------------------------- Cohorts

function CohortsSection({ clientId }: { clientId: string }): JSX.Element {
  // Keyed under the client, so a cohort edit anywhere invalidates this
  // list by prefix — including from the cohort detail page, which is the
  // cross-view case that had no answer before ADR-0056.
  // A capped PREVIEW off the GLOBAL list, not the unpaged per-client one:
  // the same rule the roster below and the cohort's member list already
  // follow. Ten rows, the true total, and a link to the paged view — an
  // embedded list on a detail page is never the place to page in second.
  const previewParams = { clientId, limit: COHORT_PREVIEW, offset: 0 }
  const cohortsQuery = useQuery({
    queryKey: queryKeys.cohortList(previewParams),
    queryFn: () => adminCohortClient.listAll(previewParams),
  })

  const cohorts = cohortsQuery.data?.cohorts ?? null
  const cohortTotal = cohortsQuery.data?.total ?? 0
  const error =
    cohortsQuery.error !== null
      ? messageForCohortError(cohortsQuery.error)
      : null

  return (
    <section aria-label="Cohorts" className="bg-surface-card rounded-token-card p-6 mt-4">
      {/* `items-center`, not `items-baseline`: a button has no text baseline to
          align an h2 against, so baseline alignment sits it a few pixels low. */}
      <SectionHeader
        title="Cohorts"
        meta={<FieldHelp label="client cohorts">
            Groups with their own access window and enrollment defaults. A
            client can run several cohorts on different schedules.
          </FieldHelp>}
        actions={<>{/* Gated on the TOTAL, not the preview's length: a client whose
              cohorts all sit past the cap still needs the way to them. */}{cohortTotal > 0 && (
            <Link
              to={`/admin/cohorts?clientId=${clientId}`}
              className={buttonVariants({ variant: 'secondary', size: 'sm' })}
            >
              Open cohorts
            </Link>
          )}<Link
            to={`/admin/clients/${clientId}/cohorts/new`}
            className={buttonVariants({ variant: 'primary', size: 'sm' })}
          >
            New cohort
          </Link></>}
        className="mb-1"
      />
      {error !== null && (
        <Notice variant="danger">
          {error}
        </Notice>
      )}
      {error === null && cohorts === null && (
        <p className="text-text-secondary text-body">Loading cohorts…</p>
      )}
      {cohorts !== null && cohorts.length === 0 && (
        <p className="text-text-secondary text-body">
          No cohorts yet. Create one before adding students — it carries the
          dates and defaults they inherit.
        </p>
      )}
      {/* ABOVE the table, matching the roster preview's sentence shape:
          the operator learns the list is partial before reading it. */}
      {cohorts !== null && cohortTotal > cohorts.length && (
        <p className="text-text-secondary text-caption mb-3">
          Showing the first {cohorts.length} of {cohortTotal} — open the cohorts
          list for the paged, searchable view.
        </p>
      )}
      {cohorts !== null && cohorts.length > 0 && (
        <table className="w-full text-table">
          <thead>
            <tr className="text-left text-text-secondary border-b border-border-default">
              <th className="py-2 font-medium">Name</th>
              <th className="py-2 font-medium">Starts</th>
              <th className="py-2 font-medium">Ends</th>
              <th className="py-2 font-medium">Members</th>
            </tr>
          </thead>
          <tbody>
            {cohorts.map((c) => (
              <tr
                key={c.cohortId}
                className={TABLE_ROW_NESTED}
              >
                <td className="py-2">
                  <Link
                    to={`/admin/clients/${clientId}/cohorts/${c.cohortId}`}
                    className="underline"
                  >
                    {c.name}
                  </Link>
                  {c.isInternal === true && (
                    <span className="ml-2" title="Internal: excluded from customer analytics. Not an access flag.">
                      <InternalBadge />
                    </span>
                  )}
                </td>
                <td className="py-2">{c.scheduledStartOn ?? 'on activation'}</td>
                <td className="py-2">{c.accessEndsOn}</td>
                <td className="py-2">{c.memberCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

// ------------------------------------------------------- Current enrollments by tier

/**
 * Date/access-eligible enrollment periods grouped by immutable tier snapshot.
 */
function CurrentEnrollmentTiersSection({
  client,
}: {
  client: ClientDetail
}): JSX.Element {
  return (
    <section
      aria-label="Current enrollments by tier"
      className="bg-surface-card rounded-token-card p-6 mt-4"
    >
      <SectionHeader
        title="Current enrollments by tier"
        meta={<FieldHelp label="current enrollments by tier">
          Enrollment periods eligible for study access today, grouped by their
          locked tier. Future, suspended and ended periods are excluded. This is
          operational information, not a commercial entitlement.
        </FieldHelp>}
        className="mb-3"
      />
      {/* One row, tiers as columns: four numbers an operator glances at,
          not a list to scan. `table-fixed` so the columns split the width
          evenly instead of sizing to their label text. */}
      <table className="w-full table-fixed text-table">
        <thead>
          <tr className="text-left border-b border-border-default">
            {ENROLLMENT_TIERS.map((tier) => (
              <th key={tier} className="py-2 font-medium">
                {tier}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className={TABLE_ROW_NESTED}>
            {ENROLLMENT_TIERS.map((tier) => (
              <td key={tier} className="py-2">
                {client.currentEnrollmentCountByTier[tier]}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </section>
  )
}

function RosterSection({ client }: { client: ClientDetail }): JSX.Element {
  // Its own cache entry, distinct from the full students list, because the
  // params differ — the preview's `limit: 10` must never be served to the
  // paged list or vice versa.
  const params = { clientId: client.clientId, limit: ROSTER_PREVIEW }
  const rosterQuery = useQuery({
    queryKey: queryKeys.studentList(params),
    queryFn: () => adminStudentClient.list(params),
  })
  // Both answers ride the client row this page already has, rather than an
  // aggregate over a fetched list of cohorts.
  //
  // That distinction is not tidiness. `joinableCohortCount` is computed by
  // the server over EVERY cohort; the old predicate walked whatever the
  // cohort list happened to return. While that list was unpaged the two
  // agreed — but the moment it is bounded they diverge, and they diverge in
  // the worst direction: the list sorts soonest-start-first, so a ceiling
  // keeps the oldest (ended) rows and this would report "every cohort has
  // ended" for a client with live ones, disabling batch entry with a
  // sentence that is simply false. Reading the count cannot drift that way,
  // and costs nothing — it is already in the payload.
  const hasCohorts = client.cohortCount > 0
  const hasActiveCohorts = client.joinableCohortCount > 0

  const students = rosterQuery.data?.students ?? []
  const total = rosterQuery.data?.total ?? 0
  const status: LoadStatus = rosterQuery.isPending
    ? 'loading'
    : rosterQuery.isError
      ? 'error'
      : 'loaded'

  return (
    <section aria-label="Students" className="bg-surface-card rounded-token-card p-6 mt-4">
      <SectionHeader
        title={<>Students{status === 'loaded' ? ` (${total})` : ''}</>}
        meta={<FieldHelp label="students in this client">
            Active students in this client. Use the full students view for
            searching, sorting, and bulk operations.
          </FieldHelp>}
        actions={<>{/* `?clientId=` pre-answers the batch form's first step — the
              operator has just been looking at this tenant.

              `primary`, like the Cohorts header's "New cohort": the weights
              carry the create-vs-navigate difference in these section
              headers, and this one CREATES. Outline here read as the
              disabled state of a button, not a quieter sibling.

              And when we already KNOW the batch form can only answer
              "create a cohort first" — batch creation is create-INTO-a-
              cohort — the button says so up front instead of letting the
              operator discover it a page later. Only on a POSITIVE answer:
              while cohorts load, or if their fetch failed, the link stays
              live, because the batch page degrades to the same guidance. */}{!hasActiveCohorts ? (
            <>
              <span className="text-text-secondary text-caption">
                {hasCohorts
                  ? 'Every cohort has ended — extend one, or create a new one above.'
                  : 'Needs a cohort first — create one above.'}
              </span>
              <Button variant="primary" size="sm" disabled>
                Enroll several students
              </Button>
            </>
          ) : (
            <Link
              to={`/admin/students/batch?clientId=${client.clientId}`}
              className={buttonVariants({ variant: 'primary', size: 'sm' })}
            >
              Enroll several students
            </Link>
          )}{/* Only once there is a roster to open: a link to a filtered
              list we already know is empty is a promise of results that
              are not there. Same positive-answer rule as the batch button
              — while loading, or after a roster fetch failure, the link
              stays, because the full page may still have the answer. */}{!(status === 'loaded' && students.length === 0) && (
            <Link
              to={`/admin/students?clientId=${client.clientId}`}
              className={buttonVariants({ variant: 'secondary', size: 'sm' })}
            >
              Open in students
            </Link>
          )}</>}
        className="mb-3"
      />

      {status === 'loading' && (
        <p className="text-text-secondary text-body">Loading students…</p>
      )}
      {status === 'error' && (
        <p role="alert" className="text-text-secondary text-body">
          Could not load students.
        </p>
      )}
      {/* The order of operations is cohort-first, and the empty state now says
          so instead of pointing straight at student creation. A student needs a
          cohort to inherit its dates, tier and classification from; created
          without one they need an individual access end typed by hand, which is
          the exception the cohort exists to avoid. Sending an operator to
          "add a student" on a client with no cohorts was inviting them into
          the harder path first.

          The link also carries `?clientId=`, because the create form otherwise
          defaults to the OPERATOR's own client — actively wrong for someone who
          has just been looking at this one. */}
      {status === 'loaded' && students.length === 0 && (
        <p className="text-text-secondary text-body">
          {hasCohorts ? (
            <>
              No students yet.{' '}
              <Link
                to={`/admin/students/new?clientId=${client.clientId}`}
                className="underline"
              >
                Add one
              </Link>{' '}
              or{' '}
              <Link
                to={`/admin/students/batch?clientId=${client.clientId}`}
                className="underline"
              >
                paste a list
              </Link>
              .
            </>
          ) : (
            <>
              No students yet — and no cohorts to put them in.{' '}
              <Link
                to={`/admin/clients/${client.clientId}/cohorts/new`}
                className="underline"
              >
                Create a cohort
              </Link>{' '}
              first: it carries the start date, access end, tier and
              classification its members inherit.
            </>
          )}
        </p>
      )}
      {status === 'loaded' && students.length > 0 && (
        <>
          {/* The shared preview shell — same chrome as the cohort page's
              Members block. The NAME is the link; the trailing Edit
              column this table used to carry is the pattern the
              conventions ban. */}
          {/* ABOVE the table, like the cohort Members block: the operator
              learns this is a truncated preview before reading ten rows,
              not after. Same sentence shape on both surfaces. */}
          {total > students.length && (
            <p className="text-text-secondary text-caption mb-3">
              Showing the first {students.length} of {total} — Open in
              students for the paged, searchable list.
            </p>
          )}
          {/* Invitation rides the preview (PROV-ALERTS): Sentry says a send
              failed NOW; this table says WHO, on the page an operator
              already has open. The shared cell keeps this and the global
              roster from ever disagreeing about what `failed` looks like. */}
          <PreviewTable headers={['Name', 'Email', 'Tier', 'State', 'Invitation']}>
            {students.map((l) => (
              <tr key={l.userId} className={TABLE_ROW_NESTED}>
                <td className={PREVIEW_CELL}>
                  <PreviewNameLink userId={l.userId}>{l.name}</PreviewNameLink>
                </td>
                <td className={`${PREVIEW_CELL} text-text-secondary`}>{l.email}</td>
                <td className={PREVIEW_CELL}>{l.tier}</td>
                <td className={`${PREVIEW_CELL} text-text-secondary`}>
                  {l.onboarded ? 'onboarded' : 'fresh'}
                </td>
                <td className={`${PREVIEW_CELL} whitespace-nowrap`}>
                  <InviteStateCell student={l} />
                </td>
              </tr>
            ))}
          </PreviewTable>
        </>
      )}
    </section>
  )
}

// --------------------------------------------------------- HR users

function HrUsersSection({ client }: { client: ClientDetail }): JSX.Element {
  return (
    <section aria-label="HR users" className="bg-surface-card rounded-token-card p-6 mt-4">
      <SectionHeader
        title="HR users"
        meta={<FieldHelp label="client HR users">
            Staff identities are separate from student enrollments. Cohort
            access determines which enrolled students each HR user can see.
          </FieldHelp>}
        actions={<>{/* Gated on the true COUNT, not on the preview's length: the
              embedded array is capped now, and a client whose HR users all
              sit past the cap must still offer the way to see them. */}{client.hrUserCount > 0 && (
            <Link
              to={`/admin/hr-users?clientId=${client.clientId}`}
              className={buttonVariants({ variant: 'secondary', size: 'sm' })}
            >
              Open directory
            </Link>
          )}<Link
            to={`/admin/clients/${client.clientId}/hr-users/new`}
            className={buttonVariants({ variant: 'primary', size: 'sm' })}
          >
            New HR user
          </Link></>}
        className="mb-3"
      />
      {client.hrUsers.length === 0 ? (
        <p className="text-text-secondary text-body">
          No HR users in this client yet.
        </p>
      ) : (
        <>
        {/* ABOVE the table, and the same sentence shape as the roster
            preview two sections up: the operator learns this is truncated
            before reading the rows, not after. The array is capped
            server-side; `hrUserCount` is the real number. */}
        {client.hrUserCount > client.hrUsers.length && (
          <p className="text-text-secondary text-caption mb-3">
            Showing the first {client.hrUsers.length} of {client.hrUserCount} —
            open the directory for the paged, searchable list.
          </p>
        )}
        <table className="w-full text-table">
          <thead>
            <tr className="text-left border-b border-border-default">
              <th className="py-2 font-medium">Name</th>
              <th className="py-2 font-medium">Email</th>
              <th className="py-2 font-medium">Role</th>
            </tr>
          </thead>
          <tbody>
            {client.hrUsers.map((hrUser) => (
              <tr
                key={hrUser.userId}
                className={TABLE_ROW_NESTED}
              >
                <td className="py-2">
                  <Link
                    to={`/admin/clients/${client.clientId}/hr-users/${hrUser.userId}`}
                    className="underline"
                  >
                    {hrUser.name}
                  </Link>
                </td>
                <td className="py-2 text-text-secondary">{hrUser.email}</td>
                <td className="py-2">{hrUser.role}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </>
      )}
    </section>
  )
}

// ----------------------------------------------------------- Delete

function DangerSection({ client }: { client: ClientDetail }): JSX.Element {
  const queryClient = useQueryClient()
  // The server refuses with 409 `client_has_students` while any student
  // remains, and this response already carries the count — so the refusal is
  // knowable before the click. Offering a button that cannot work, and
  // answering with an error only after a confirm step, is a worse version of
  // saying so up front. Matches the cohort page's delete guard.
  //
  // `studentCount` is active students only (HR users and soft-deleted rows
  // excluded), which is exactly the predicate the server's guard uses.
  const blockedBy = client.studentCount

  const remove = useMutation({
    mutationFn: () => adminClientsApi.remove(client.clientId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.clientLists }),
  })

  const deleting = remove.isPending
  const done = remove.isSuccess
  const error =
    remove.error !== null ? messageForClientError(remove.error) : null

  if (done) {
    return (
      <section className="bg-surface-card rounded-token-card p-6 mt-4">
        <p role="status" className="text-body">
          Client deleted.{' '}
          <Link to="/admin/clients" className="underline">
            Back to clients
          </Link>
          .
        </p>
      </section>
    )
  }

  return (
    <HighConsequenceActionPanel
      dangerZone
      title="Delete client"
      className="mt-4"
      description={
        blockedBy > 0 ? (
          <>
            Not while this client has students &mdash; {blockedBy}{' '}
            {blockedBy === 1 ? 'student is' : 'students are'} still assigned to
            this client. Winding down a live client is a different operation
            (revocation, retention, certificates), so remove or move them
            first.
          </>
        ) : (
          <>
            For a client created by mistake. Soft delete &mdash; the row is
            retained under the APPI schedule, and the slug stays claimed.
          </>
        )
      }
      error={error}
    >
      {/*
        Behavior change, deliberate (ADR-0056): the confirm was an inline row
        that appeared in place of the button. Same two answers, same wording,
        but now with focus management, Escape-to-cancel and `aria-modal` —
        which a hand-rolled row cannot have, and which matter most on the one
        control in the page that destroys something.
      */}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="dangerOpen" disabled={blockedBy > 0}>
            Delete client
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {client.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Soft delete — the row is retained under the APPI schedule, and
              the slug stays claimed, so it cannot be reused for a new
              client.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => remove.mutate()}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : 'Yes, delete'}
            </AlertDialogAction>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </HighConsequenceActionPanel>
  )
}
